import React from 'react';

import IconButton from '@mui/material/IconButton';

import Icon from 'src/components/ui/Icon';

import useAlerts from 'src/hooks/useAlerts';
import useLoginModal from 'src/hooks/useLoginModal';

import {
    useLikePostMutation,
    useDislikePostMutation,
    useLikeNewsPostMutation,
    useDislikeNewsPostMutation,
    useLikeCommentMutation,
    useDislikeCommentMutation,
} from 'src/hooks/__generated__/queries';
import type { PostsQuery, NewsPost, PostCommentsQuery, Post } from 'src/types/__generated__/graphql';
import { AnalyticsEvent } from 'src/utils/analytics';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import useUser from 'src/hooks/useUser';

const StyledIconButton = styledComponent(IconButton)`
    width: 3rem;
    padding: 0.75rem 0.25rem;
`;

type Props = {
    post?: ArrayElement<PostsQuery['posts']>;
    newsPost?: Partial<NewsPost>;
    comment?: ArrayElement<PostCommentsQuery['comments']>;
    iconProps?: Omit<IconProps, 'name'>;
};

const LikeDislikeIcon: React.FC<Props> = ({ post, newsPost, comment, iconProps }) => {
    const { addAlert } = useAlerts();
    const { createAuthenticatedHandler } = useLoginModal();
    const currentUser = useUser();
    const { likePostInCache, dislikePostInCache, likeNewsPostInCache, dislikeNewsPostInCache } = useApolloCacheUpdate();

    const { logEventDynamic } = useDynamicLogEvents();

    const [likePostMutation, { loading: likePostLoading }] = useLikePostMutation({
        onCompleted() {
            likePostInCache(post as Post);
        },
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const [dislikePostMutation, { loading: dislikePostLoading }] = useDislikePostMutation({
        onCompleted() {
            dislikePostInCache(post as Post);
        },
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const [likeCommentMutation, { loading: likeCommentLoading }] = useLikeCommentMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const [dislikeCommentMutation, { loading: dislikeCommentLoading }] = useDislikeCommentMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const [likeNewsPostMutation, { loading: likeNewsPostLoading }] = useLikeNewsPostMutation({
        onCompleted() {
            likeNewsPostInCache(newsPost as NewsPost);
        },
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const [dislikeNewsPostMutation, { loading: dislikeNewsPostLoading }] = useDislikeNewsPostMutation({
        onCompleted() {
            dislikeNewsPostInCache(newsPost as NewsPost);
        },
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
    });

    const loading =
        likePostLoading ||
        dislikePostLoading ||
        likeCommentLoading ||
        dislikeCommentLoading ||
        likeNewsPostLoading ||
        dislikeNewsPostLoading;

    const handleLikeClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        if (post) {
            await likePostMutation({
                variables: {
                    postId: post.id,
                },
            });
        }
        if (comment?.id) {
            await likeCommentMutation({
                variables: {
                    commentId: comment.id,
                },
                refetchQueries: ['PostComments'],
                awaitRefetchQueries: true,
            });
        }
        if (newsPost?.id) {
            await likeNewsPostMutation({
                variables: {
                    newsPostId: newsPost.id,
                },
            });
        }
    };
    const handleUnLikeClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        if (post) {
            await dislikePostMutation({
                variables: {
                    postId: post.id,
                },
            });
        }
        if (comment?.id) {
            await dislikeCommentMutation({
                variables: {
                    commentId: comment.id,
                },
                refetchQueries: ['PostComments'],
                awaitRefetchQueries: true,
            });
        }
        if (newsPost?.id) {
            await dislikeNewsPostMutation({
                variables: {
                    newsPostId: newsPost.id,
                },
            });
        }
    };

    const isLiked = post?.likedByCurrentUser ?? newsPost?.likedByCurrentUser ?? comment?.likedByCurrentUser;
    const handleClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        logEventDynamic(isLiked && currentUser ? AnalyticsEvent.Unlike : AnalyticsEvent.Like);
        await createAuthenticatedHandler(async () => {
            if (isLiked) {
                await handleUnLikeClick(e);
            } else {
                await handleLikeClick(e);
            }
        });
    };
    return (
        <StyledIconButton onClick={handleClick} disabled={loading}>
            <Icon size="1.25rem" name={isLiked ? 'HeartFillRed' : 'HeartEmpty'} {...iconProps} />
        </StyledIconButton>
    );
};

export default LikeDislikeIcon;
